import type { GameSettings, PlayerProgress, SaveGame, TowerJournal } from './types';

const DB_NAME = 'infinite-stack';
const DB_VERSION = 1;
const STORE_NAME = 'saves';
const SAVE_KEY = 'current';
const SETTINGS_KEY = 'infinite-stack:settings';
const JOURNAL_KEY = 'infinite-stack:journal';
type StoredSave = Omit<SaveGame, 'version'> & { version: number };

export const DEFAULT_SETTINGS: GameSettings = {
  sound: true,
  haptic: true,
  reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  nightMode: false,
};

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveGame(save: SaveGame): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).put(save, SAVE_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function loadGame(): Promise<SaveGame | null> {
  const database = await openDatabase();
  const save = await new Promise<StoredSave | undefined>((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(SAVE_KEY);
    request.onsuccess = () => resolve(request.result as StoredSave | undefined);
    request.onerror = () => reject(request.error);
  });
  database.close();
  if (!save || ![1, 2, 3, 4].includes(save.version)) return null;
  return { ...save, version: 4 };
}

export async function clearSave(): Promise<void> {
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    transaction.objectStore(STORE_NAME).delete(SAVE_KEY);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

// Day/night choice shared with the Play index and the other games on this origin.
const SHARED_THEME_KEY = 'pandu-play:theme';

export function withSharedTheme(settings: GameSettings): GameSettings {
  try {
    const theme = localStorage.getItem(SHARED_THEME_KEY);
    if (theme === 'night' || theme === 'day') return { ...settings, nightMode: theme === 'night' };
  } catch {
    // Storage unavailable: keep the game's own setting.
  }
  return settings;
}

export function loadSettings(): GameSettings {
  try {
    return withSharedTheme({ ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}') as Partial<GameSettings> });
  } catch {
    return withSharedTheme({ ...DEFAULT_SETTINGS });
  }
}

export function saveSettings(settings: GameSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  localStorage.setItem(SHARED_THEME_KEY, settings.nightMode ? 'night' : 'day');
}

function freshJournal(): TowerJournal {
  return {
    version: 1,
    currentStudyId: 1,
    currentStartedAt: new Date().toISOString(),
    entries: [],
  };
}

export function loadJournal(): TowerJournal {
  try {
    const stored = JSON.parse(localStorage.getItem(JOURNAL_KEY) ?? 'null') as TowerJournal | null;
    if (!stored || stored.version !== 1) return freshJournal();
    return {
      ...stored,
      currentStudyId: Math.max(1, stored.currentStudyId),
      entries: stored.entries.slice(0, 24),
    };
  } catch {
    return freshJournal();
  }
}

export function saveJournal(journal: TowerJournal): void {
  localStorage.setItem(JOURNAL_KEY, JSON.stringify(journal));
}

export function archiveStudy(journal: TowerJournal, progress: PlayerProgress): TowerJournal {
  const next: TowerJournal = {
    version: 1,
    currentStudyId: journal.currentStudyId + 1,
    currentStartedAt: new Date().toISOString(),
    entries: [{
      id: journal.currentStudyId,
      startedAt: journal.currentStartedAt,
      endedAt: new Date().toISOString(),
      score: progress.score,
      height: progress.height,
      highestTier: progress.highestTier,
      precisionCount: progress.precisionCount,
      longestPrecisionChain: progress.longestPrecisionChain,
    }, ...journal.entries].slice(0, 24),
  };
  saveJournal(next);
  return next;
}
